import { STALLED_AFTER_DAYS } from '../../api/selectors'
import { AdvancedTrackDefinition, AdvancedTrackLead, AdvancedTrackSummary } from '../../api/types'
import { PatientHistoryEntry } from '../../api/useAdvancedPatientHistory'
import { acceptanceCriteriaFor } from '../../dashboard/tracks/trackAcceptance'
import {
  TrackStartOption,
  TrackStatus,
  findTrack,
  switchTargets,
  trackStartOptions,
  trackStatus,
  trackStatusLabel,
  trackTitle,
} from '../../dashboard/tracks/trackTransitions'
import { TrackActions } from '../../dashboard/tracks/useTrackActions'
import { TagAction, TagDecoration } from './ItemTag'
import { PlacedItem } from './PlannerCell'
import { CarePlanActions } from './carePlanActions'
import { MONTH_LABELS, PSYCHIATRIC_SUPERVISION_TRACK_KEY, isTrackRowKey } from './categories'
import {
  DueSuggestion,
  acceptedStartWeek,
  dueSuggestions,
  endedRunOverlaps,
  isLiveItem,
  itemStartWeek,
  planWeekAt,
  planWeekOf,
  runEndedByMove,
} from './livePlan'

type PlanTrackContext = {
  catalog: AdvancedTrackDefinition[]
  tracks: AdvancedTrackSummary[]
  history: PatientHistoryEntry[]
  trackRows: { key: string }[]
  trackActions: TrackActions
  actions: CarePlanActions
  viewOnly: boolean
  canActOnTracks: boolean
  // False while live track status is missing: bars then draw as a plan only.
  hasLiveState: boolean
  // The real Month 1; `planTime` may be the test clock's.
  anchor: string | null
  planTime: { anchor: string | null; now: Date }
  stalledAfterDays?: number
  currentMonth: number | null
}

// Live track state and track actions for each bar on the Master Care Plan (ADR-092, ADR-094).
export const planTrackDecorations = ({
  catalog,
  tracks,
  history,
  trackRows,
  trackActions,
  actions,
  viewOnly,
  canActOnTracks,
  hasLiveState,
  anchor,
  planTime,
  currentMonth,
  stalledAfterDays = STALLED_AFTER_DAYS,
}: PlanTrackContext) => {
  const { now } = planTime
  // Opening intake on a bar planned for later pulls it back to this week.
  const moveToNow = (rowKey: string, item: PlacedItem) => () => {
    const week = planWeekAt(planTime.anchor, now)
    if (itemStartWeek(item) > week) {
      actions.scheduleItem(rowKey, item.id, week)
    }
  }
  // Nothing in the future is running: moving a running track's bar past this week ends it first, via the end dialog.
  const confirmMove = (_rowKey: string, item: PlacedItem, toWeek: number, apply: () => void) => {
    const track = runEndedByMove(tracks, item, toWeek, planWeekAt(planTime.anchor, now))
    if (track) {
      trackActions.actOn(item.id)
      trackActions.openEnd({ track, onEnded: apply })
      return
    }
    apply()
  }

  // Psychiatric supervision runs from acceptance, so it is opened and accepted in one step (ADR-098).
  const openStartNow = (trackKey: string, lead?: AdvancedTrackLead) => {
    const title = trackTitle(catalog, trackKey)
    trackActions.openAccept({
      title,
      startsNow: true,
      criteria: acceptanceCriteriaFor(
        { trackKey, enrollmentId: null, openedAt: null, startsOnAcceptance: true },
        title,
        tracks,
        history
      ),
      confirm: () => trackActions.start({ trackKey, lead, acceptNow: true }),
    })
  }

  const startOptionFor = (trackKey: string) =>
    trackStartOptions(catalog, tracks).find((candidate) => candidate.track.trackKey === trackKey)

  const plannedLeadFor = (rowKey: string, trackKey: string) =>
    actions.itemsIn(rowKey).find((item) => item.trackKey === trackKey)?.lead

  const beginTrack = ({ track, replaces }: TrackStartOption, lead?: AdvancedTrackLead, onStarted?: () => void) => {
    if (replaces) {
      trackActions.openEnd({ track: replaces, endReason: 'discontinued', next: track.trackKey, nextLead: lead })
      return
    }
    if (track.startsOnAcceptance) {
      openStartNow(track.trackKey, lead)
      return
    }
    trackActions.start({ trackKey: track.trackKey, lead }, onStarted)
  }

  const beginLabel = ({ track, replaces }: TrackStartOption, openLabel: string) => {
    if (replaces) {
      return `Switch to ${track.title}…`
    }

    return track.startsOnAcceptance ? `Start ${track.title.toLowerCase()}…` : openLabel
  }

  const startActionsFor = (
    trackKey: string,
    openLabel: string,
    lead?: AdvancedTrackLead,
    onStarted?: () => void
  ): TagAction[] => {
    const option = startOptionFor(trackKey)

    return option ? [{ label: beginLabel(option, openLabel), onClick: () => beginTrack(option, lead, onStarted) }] : []
  }

  const suggestions = dueSuggestions(
    trackRows.map((row) => ({ key: row.key, items: actions.itemsIn(row.key) })),
    tracks,
    planTime.anchor,
    currentMonth
  )
  const suggestionFor = (rowKey: string, trackKey: string | undefined) =>
    trackKey ? suggestions.find((due) => due.rowKey === rowKey && due.trackKey === trackKey) : undefined
  const runSuggestion = (suggestion: DueSuggestion) => {
    if (suggestion.kind === 'start') {
      const option = startOptionFor(suggestion.trackKey)
      if (option) {
        beginTrack(option, plannedLeadFor(suggestion.rowKey, suggestion.trackKey))
      }
      return
    }
    const running = tracks.find((track) => track.enrollmentId === suggestion.enrollmentId)
    if (running) {
      trackActions.openEnd({ track: running })
    }
  }
  const promptFor = (rowKey: string, trackKey: string | undefined): TagDecoration['prompt'] => {
    const suggestion = suggestionFor(rowKey, trackKey)
    if (!canActOnTracks || !suggestion) {
      return undefined
    }

    const label =
      suggestion.kind === 'start' && findTrack(catalog, suggestion.trackKey)?.startsOnAcceptance ? 'Start' : undefined

    return { kind: suggestion.kind, onClick: () => runSuggestion(suggestion), label }
  }
  const rescheduleFor = (rowKey: string, item: PlacedItem): TagDecoration['reschedule'] => {
    if (viewOnly || currentMonth == null) {
      return undefined
    }
    const months = MONTH_LABELS.flatMap((label, month) =>
      month >= currentMonth && month !== item.startMonth ? [{ month, label }] : []
    )
    if (months.length === 0) {
      return undefined
    }

    return { months, onPick: (month) => actions.moveItem(rowKey, item.id, month) }
  }

  // Plan edits, not care actions: they change the calendar and leave any running track alone.
  const planActionsFor = (rowKey: string, item: PlacedItem, trackKey: string): TagAction[] => {
    if (viewOnly || trackKey === PSYCHIATRIC_SUPERVISION_TRACK_KEY) {
      return []
    }
    if (isLiveItem(item)) {
      return [
        {
          label: 'Add to plan',
          onClick: () => actions.placeItem(rowKey, item.startMonth, item.option, item.months, trackKey),
        },
      ]
    }

    return [{ label: 'Remove from plan', onClick: () => actions.removeItem(rowKey, item.id) }]
  }

  const unlinkedDecoration = (rowKey: string, item: PlacedItem): TagDecoration => ({
    status: 'planned',
    label: 'Not linked to a catalog track. Remove it and add the track again.',
    actions: viewOnly ? [] : [{ label: 'Remove from plan', onClick: () => actions.removeItem(rowKey, item.id) }],
    locked: false,
  })

  const plannedDecoration = (rowKey: string, item: PlacedItem, trackKey: string): TagDecoration => {
    const locked = isLiveItem(item)
    const fixed = trackKey === PSYCHIATRIC_SUPERVISION_TRACK_KEY
    const late = suggestionFor(rowKey, trackKey)?.kind === 'start'
    const plannedLead = trackActions.leads.leadLabel(item.lead?.providerUid ?? null)
    const leadActions: TagAction[] =
      viewOnly || locked
        ? []
        : [
            {
              label: item.lead ? 'Change who leads…' : 'Assign a lead…',
              onClick: () =>
                trackActions.openPlannedLead(trackKey, item.lead ?? null, (lead) =>
                  actions.setItemLead(rowKey, item.id, lead)
                ),
            },
          ]

    return {
      status: 'planned',
      label: [
        fixed ? "Starts after the psychiatrist's intake call" : 'Not started',
        plannedLead && `led by ${plannedLead}`,
      ]
        .filter(Boolean)
        .join(' · '),
      actions: [
        ...(canActOnTracks ? startActionsFor(trackKey, 'Open intake', item.lead, moveToNow(rowKey, item)) : []),
        ...leadActions,
        ...planActionsFor(rowKey, item, trackKey),
      ],
      locked,
      prompt: promptFor(rowKey, trackKey),
      late,
      reschedule: late && !fixed ? rescheduleFor(rowKey, item) : undefined,
      fixed,
    }
  }

  const openAcceptFor = (rowKey: string, item: PlacedItem, track: AdvancedTrackSummary) => () => {
    const title = trackTitle(catalog, track.trackKey)
    const startsOnAcceptance = Boolean(findTrack(catalog, track.trackKey)?.startsOnAcceptance)
    trackActions.openAccept({
      title,
      startsNow: startsOnAcceptance,
      criteria: acceptanceCriteriaFor(
        { trackKey: track.trackKey, enrollmentId: track.enrollmentId, openedAt: track.openedAt, startsOnAcceptance },
        title,
        tracks,
        history
      ),
      confirm: (startsOn) => {
        // The bar only moves if it was planned later; the first accepted track opens Month 1 (ADR-100).
        const monthOne = anchor ?? `${startsOn}T00:00:00.000Z`
        const startWeek = startsOn ? planWeekOf(monthOne, `${startsOn}T00:00:00.000Z`) : null
        const moveBar =
          startWeek !== null && !isLiveItem(item) && startWeek < itemStartWeek(item)
            ? () => actions.scheduleItem(rowKey, item.id, startWeek)
            : undefined
        trackActions.accept(track.enrollmentId, startsOn, moveBar)
      },
    })
  }

  const liveDecoration = (rowKey: string, item: PlacedItem, track: AdvancedTrackSummary): TagDecoration => {
    const { trackKey } = track
    const status = trackStatus(track, stalledAfterDays)
    const openAccept = openAcceptFor(rowKey, item, track)
    const leadChange: TagAction = { label: 'Change who leads…', onClick: () => trackActions.openLead(track) }
    const cancel: TagAction = {
      label: status === 'intake' ? 'Cancel intake' : 'Cancel before it starts',
      onClick: () => trackActions.cancelIntake(track),
    }
    const runningActions: TagAction[] = [
      { label: 'End track…', onClick: () => trackActions.openEnd({ track }) },
      ...switchTargets(catalog, track).map((target) => ({
        label: `Switch to ${target.title}…`,
        onClick: () =>
          trackActions.openEnd({
            track,
            endReason: 'discontinued',
            next: target.trackKey,
            nextLead: plannedLeadFor(rowKey, target.trackKey),
          }),
      })),
      leadChange,
    ]
    const reopenActions = startActionsFor(trackKey, 'Open intake again')
    const actionsByStatus: Record<TrackStatus, TagAction[]> = {
      intake: [{ label: 'Accept and set a start date…', onClick: openAccept }, leadChange, cancel],
      scheduled: [leadChange, cancel],
      active: runningActions,
      stalled: runningActions,
      completed: reopenActions,
      closed: reopenActions,
    }
    const lead = trackActions.leads.leadLabel(track.providerUid)

    return {
      status,
      label: lead
        ? `${trackStatusLabel(track, stalledAfterDays)} · led by ${lead}`
        : trackStatusLabel(track, stalledAfterDays),
      actions: [...(canActOnTracks ? actionsByStatus[status] : []), ...planActionsFor(rowKey, item, trackKey)],
      locked: isLiveItem(item),
      fixed: trackKey === PSYCHIATRIC_SUPERVISION_TRACK_KEY,
      acceptedAtColumn: acceptedStartWeek(track, planTime.anchor),
      prompt:
        status === 'intake' && canActOnTracks ? { kind: 'accept', onClick: openAccept } : promptFor(rowKey, trackKey),
    }
  }

  const runShownOn = (item: PlacedItem, trackKey: string) => {
    const latest = tracks.find((candidate) => candidate.trackKey === trackKey)
    const shown = latest && (isLiveItem(item) || !latest.endedAt || endedRunOverlaps(latest, item, planTime.anchor))

    return shown ? latest : undefined
  }

  const fromBar = (item: PlacedItem, decoration: TagDecoration): TagDecoration => {
    const tagged = (onClick: () => void) => () => {
      trackActions.actOn(item.id)
      onClick()
    }

    return {
      ...decoration,
      actions: decoration.actions.map((action) => ({ ...action, onClick: tagged(action.onClick) })),
      prompt: decoration.prompt && { ...decoration.prompt, onClick: tagged(decoration.prompt.onClick) },
      busy: trackActions.busyOn(item.id),
      failure: trackActions.failureFor(item.id) ?? undefined,
      onDismissFailure: trackActions.dismissFailure,
    }
  }

  const decorationFor = (rowKey: string, item: PlacedItem): TagDecoration | undefined => {
    if (!item.trackKey && isTrackRowKey(rowKey)) {
      return unlinkedDecoration(rowKey, item)
    }
    if (!item.trackKey || !hasLiveState) {
      return undefined
    }
    const track = runShownOn(item, item.trackKey)

    return fromBar(item, track ? liveDecoration(rowKey, item, track) : plannedDecoration(rowKey, item, item.trackKey))
  }

  return { decorationFor, confirmMove }
}
